import { useParams } from 'react-router-dom';
import type { SelectableBranchDto } from '../types/SelectableBranchDto';
import { query } from './query';
import type { TillDto } from '../types/TillDto';
import type { ComboboxItem } from '@mantine/core';
import { useMemo } from 'react';
import { formatTillId } from '../formatting';

export function useBranchOptions() {
	const { organisation } = useParams();

	const pharmaciesQuery = query<SelectableBranchDto>(
		`${organisation}/branches/options`,
	);

	return pharmaciesQuery;
}

export function useTillOptions<T>(
	selectedBranchId: string,
	branchOptions?: SelectableBranchDto,
) {
	const { organisation } = useParams();

	const tillsQuery = query<TillDto[]>(`${organisation}/tills/options`);

	const filteredTills = useMemo(() => {
		const branchName = branchOptions?.branches
			.filter((b) => b.id.toString() === selectedBranchId)
			.map((b) => b.name);

		if (branchName && branchName[0] !== '') {
			const tills = tillsQuery.data?.value.filter(
				(t) => t.branch === branchName[0],
			);

			return tills?.flatMap((p) => {
				const comboBoxItem: ComboboxItem = {
					label: `${p.name}`,
					value: formatTillId(p.name, p.id.toString(), selectedBranchId),
				};

				return comboBoxItem;
			});
		}
	}, [branchOptions, tillsQuery.data?.value, selectedBranchId]);

	const unfilteredTills = useMemo(() => {
		return tillsQuery.data?.value.flatMap((p) => {
			const comboBoxItem: ComboboxItem = {
				label: `${p.name} - ${p.name}`,
				value: p.name,
			};

			return comboBoxItem;
		});
	}, [tillsQuery.data?.value]);

	const tillsPlaceholder = useMemo(() => {
		if (selectedBranchId && selectedBranchId.replace(' ', '') === '') {
			return 'Please select a branch...';
		}

		if (filteredTills && filteredTills.length < 1) {
			return 'No tills associated with branch...';
		}

		return 'Please select a till...';
	}, [selectedBranchId, filteredTills]);

	return { tillsQuery, filteredTills, unfilteredTills, tillsPlaceholder };
}
